import React, { useEffect, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, Loader2, ChevronDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Mesa, MesaForma } from '@/types';
import { mesaService } from '@/services/mesaService';
import { MesaCard } from './MesaCard';
import { MesaEditModal } from './MesaEditModal';

interface MesasDistribucionProps {
  eventId: string;
  mesas: Mesa[];
  onMesasChange: (mesas: Mesa[]) => void;
  canWrite?: boolean;
}

interface SortableMesaCardProps {
  mesa: Mesa;
  onEdit?: (mesa: Mesa) => void;
  onDelete?: (mesa: Mesa) => void;
}

const SortableMesaCard: React.FC<SortableMesaCardProps> = ({ mesa, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: mesa.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <MesaCard
        mesa={mesa}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={{ attributes, listeners }}
      />
    </div>
  );
};

export const MesasDistribucion: React.FC<MesasDistribucionProps> = ({
  eventId,
  mesas,
  onMesasChange,
  canWrite = true,
}) => {
  const { toast } = useToast();
  const [bulkCantidad, setBulkCantidad] = useState(10);
  const [bulkCapacidad, setBulkCapacidad] = useState(10);
  const [bulkForma, setBulkForma] = useState<MesaForma>('REDONDA');
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState<Mesa | null>(null);
  const [bulkOpen, setBulkOpen] = useState(mesas.length === 0);

  useEffect(() => {
    if (mesas.length === 0) setBulkOpen(true);
  }, [mesas.length]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleBulkCreate = async () => {
    if (bulkCantidad <= 0) return;
    setSubmitting(true);
    try {
      const startNumero = mesas.length > 0 ? Math.max(...mesas.map((m) => m.numero)) + 1 : 1;
      const startOrden = mesas.length > 0 ? Math.max(...mesas.map((m) => m.orden)) + 1 : 0;
      const created = await mesaService.bulkCreateMesas(
        { cantidad: bulkCantidad, capacidad: bulkCapacidad, forma: bulkForma, startNumero, startOrden },
        eventId,
      );
      onMesasChange([...mesas, ...created]);
      setBulkOpen(false);
      toast({
        title: 'Mesas creadas',
        description: `Se crearon ${created.length} mesa(s).`,
      });
    } catch (err) {
      console.error('Error creating mesas:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudieron crear las mesas',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (mesa: Mesa) => {
    if (!confirm(`¿Eliminar la mesa ${mesa.numero}? Los invitados asignados quedarán sin mesa.`)) {
      return;
    }
    try {
      await mesaService.deleteMesa(mesa.id);
      onMesasChange(mesas.filter((m) => m.id !== mesa.id));
      toast({ title: 'Mesa eliminada' });
    } catch (err) {
      console.error('Error deleting mesa:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo eliminar la mesa',
        variant: 'destructive',
      });
    }
  };

  const handleEditSave = async (patch: { nombre: string | null; capacidad: number; forma: MesaForma }) => {
    if (!editing) return;
    try {
      const updated = await mesaService.updateMesa(editing.id, patch);
      onMesasChange(mesas.map((m) => (m.id === updated.id ? updated : m)));
      toast({ title: 'Mesa actualizada' });
    } catch (err) {
      console.error('Error updating mesa:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo actualizar la mesa',
        variant: 'destructive',
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!canWrite) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = mesas.findIndex((m) => m.id === active.id);
    const newIndex = mesas.findIndex((m) => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(mesas, oldIndex, newIndex).map((m, idx) => ({ ...m, orden: idx }));
    onMesasChange(reordered);
    try {
      await mesaService.reorderMesas(reordered.map((m) => m.id));
    } catch (err) {
      console.error('Error reordering mesas:', err);
      toast({
        title: 'Error al reordenar',
        description: err instanceof Error ? err.message : 'No se pudo guardar el orden',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {canWrite && (
      <Collapsible open={bulkOpen} onOpenChange={setBulkOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="flex flex-row items-center justify-between cursor-pointer hover:bg-gray-50 rounded-t-lg select-none">
              <CardTitle className="text-lg">Crear mesas en lote</CardTitle>
              <ChevronDown
                className={`w-5 h-5 text-gray-500 transition-transform ${bulkOpen ? 'rotate-180' : ''}`}
              />
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div className="space-y-2">
              <Label htmlFor="bulk-cantidad">Cantidad</Label>
              <Input
                id="bulk-cantidad"
                type="number"
                min={1}
                max={100}
                value={bulkCantidad}
                onChange={(e) => setBulkCantidad(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-capacidad">Capacidad por mesa</Label>
              <Input
                id="bulk-capacidad"
                type="number"
                min={1}
                max={50}
                value={bulkCapacidad}
                onChange={(e) => setBulkCapacidad(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulk-forma">Forma</Label>
              <Select value={bulkForma} onValueChange={(v) => setBulkForma(v as MesaForma)}>
                <SelectTrigger id="bulk-forma">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="REDONDA">Redonda</SelectItem>
                  <SelectItem value="CUADRADA">Cuadrada</SelectItem>
                  <SelectItem value="RECTANGULAR">Rectangular</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleBulkCreate} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Crear
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Se enumeran de forma consecutiva a partir de la última mesa existente.
          </p>
        </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
      )}

      {mesas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            Aún no hay mesas. Crea una o varias usando el formulario de arriba.
          </CardContent>
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={mesas.map((m) => m.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {mesas.map((mesa) => (
                <SortableMesaCard
                  key={mesa.id}
                  mesa={mesa}
                  onEdit={canWrite ? (m) => setEditing(m) : undefined}
                  onDelete={canWrite ? handleDelete : undefined}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <MesaEditModal
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        mesa={editing}
        onSave={handleEditSave}
      />
    </div>
  );
};
